import { type JSX, useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import {
  type Capacidades,
  type ContatoNaLista,
  type EstadoBot,
  type ListaDeContatos,
  api,
  explicarMotivo,
} from '../lib/api.js';
import { ConversaCompleta } from './ConversaCompleta.js';
import { NomeDoAluno } from './NomeDoAluno.js';
import { BlocoDeAjuda, Vazio } from './ui.js';
import { OrigemDoContato } from './OrigemDoContato.js';
import { MarcarNaLista } from './MarcarNaLista.js';

/**
 * Conversas em duas colunas: contatos à esquerda, o fio à direita.
 *
 * O QUE ISTO RESOLVE. Havia três entradas para ler conversa com nomes que
 * competiam: a aba "Conversas" (sessões vivas no Redis, quase sempre vazia
 * porque a sessão expira em 30 min), a aba "Atendimentos" (lista por
 * protocolo, visão de auditoria) e um botão "Ver conversa" (o fio contínuo por
 * telefone). Duas coisas diferentes chamadas quase do mesmo jeito.
 *
 * Agora há uma: a agenda de quem já conversou, com o histórico ao lado. O
 * estado da sessão — que era o conteúdo da aba antiga — virou INDICADOR na
 * linha do contato, que é onde ele é útil: junto do nome de quem está
 * esperando. As ações (devolver ao bot, passar para a secretaria, encerrar
 * sessão) seguem no cabeçalho do fio, no mesmo componente de antes.
 *
 * A auditoria não sumiu: é o link "ver detalhe técnico deste atendimento" na
 * emenda de cada atendimento, dentro do fio.
 */

/** Períodos da coluna esquerda. Vazio = todo o período. */
const PERIODOS = [
  { value: '', label: 'Todo o período' },
  { value: '7', label: 'Últimos 7 dias' },
  { value: '30', label: 'Últimos 30 dias' },
  { value: '90', label: 'Últimos 90 dias' },
];

const POR_PAGINA = 30;

/** Espera antes de buscar: a cada tecla seria uma consulta por tecla. */
const ATRASO_BUSCA_MS = 350;

/**
 * Indicador de estado do bot na linha do contato.
 *
 * `null` é a maioria (sem sessão) e não ganha indicador: um selo em toda linha
 * viraria ruído, e o que precisa salta aos olhos justamente por ser raro.
 *
 * Cada caso tem TEXTO curto além da cor — num tema escuro, ou para quem não
 * distingue cores, o ponto colorido sozinho não diz nada.
 */
const INDICADOR: Record<EstadoBot, { rotulo: string; cor: string; icone: string }> = {
  bot: { rotulo: 'bot ativo', cor: 'teal', icone: '●' },
  humano: { rotulo: 'bot calado', cor: 'orange', icone: '▲' },
  pausado_auto: { rotulo: 'bot calado', cor: 'orange', icone: '▲' },
};

/** "há 2min", "ontem", "01/10". O horário relativo da lista. */
export function quando(iso: string): string {
  const agora = new Date();
  const d = new Date(iso);
  // `floor`, não `round`: com arredondamento, 30 segundos viravam "há 1min" —
  // tempo que não passou.
  const minutos = Math.max(0, Math.floor((agora.getTime() - d.getTime()) / 60_000));
  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos}min`;

  const diaDe = (x: Date): string => x.toDateString();
  if (diaDe(d) === diaDe(agora)) {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  const ontem = new Date(agora.getTime() - 24 * 60 * 60 * 1_000);
  if (diaDe(d) === diaDe(ontem)) return 'ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function Conversas({
  capacidades,
  selecionadoInicial,
  aoConsumirSelecionado,
  aoVerDetalheTecnico,
  aoMudarPendencias,
}: {
  capacidades: Capacidades;
  /** Contato a abrir já de entrada (vindo da fila de transbordos ou da home). */
  selecionadoInicial?: string | null;
  aoConsumirSelecionado?: () => void;
  aoVerDetalheTecnico?: (atendimentoId: string) => void;
  aoMudarPendencias: () => void;
}): JSX.Element {
  const [itens, setItens] = useState<ContatoNaLista[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [temMais, setTemMais] = useState(false);
  const [desconhecido, setDesconhecido] = useState(false);
  const [podeTelefone, setPodeTelefone] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [carregandoMais, setCarregandoMais] = useState(false);

  const [busca, setBusca] = useState('');
  const [buscaAtiva, setBuscaAtiva] = useState('');
  const [dias, setDias] = useState('');

  /** Contato aberto, endereçado pelo atendimento mais recente dele. */
  const [selecionado, setSelecionado] = useState<string | null>(null);
  /** Atendimento a destacar no fio, quando a busca foi por protocolo. */
  const [ancora, setAncora] = useState<string | null>(null);

  const parametros = useCallback(
    (antes?: string | null): string => {
      const p = new URLSearchParams({ limite: String(POR_PAGINA) });
      if (buscaAtiva.trim()) p.set('busca', buscaAtiva.trim());
      if (dias) p.set('dias', dias);
      if (antes) p.set('antes', antes);
      return p.toString();
    },
    [buscaAtiva, dias],
  );

  const carregar = useCallback(async () => {
    try {
      const r = await api.get<ListaDeContatos>(`/admin/contatos?${parametros()}`);
      setItens(r.itens);
      setCursor(r.cursor);
      setTemMais(r.temMais);
      setDesconhecido(r.estadoDesconhecido);
      setPodeTelefone(r.podeBuscarPorTelefone);
      setErro(null);

      // Busca por protocolo: seleciona o contato daquele atendimento e manda
      // o fio rolar até ele.
      if (r.atendimentoEncontrado) {
        setSelecionado(r.itens[0]?.atendimentoId ?? r.atendimentoEncontrado.atendimentoId);
        setAncora(r.atendimentoEncontrado.atendimentoId);
      }
    } catch (e) {
      setItens(null);
      setErro(explicarMotivo(e, 'falha ao carregar os contatos'));
    }
  }, [parametros]);

  // Busca com atraso: digitar "AP-001009" são nove consultas sem isto.
  useEffect(() => {
    const timer = setTimeout(() => setBuscaAtiva(busca), ATRASO_BUSCA_MS);
    return () => clearTimeout(timer);
  }, [busca]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Chegou de outra tela com um contato em mãos (transbordo, home).
  useEffect(() => {
    if (!selecionadoInicial) return;
    setSelecionado(selecionadoInicial);
    setAncora(null);
    aoConsumirSelecionado?.();
  }, [selecionadoInicial, aoConsumirSelecionado]);

  async function carregarMais(): Promise<void> {
    if (!cursor || carregandoMais) return;
    setCarregandoMais(true);
    try {
      const r = await api.get<ListaDeContatos>(`/admin/contatos?${parametros(cursor)}`);
      setItens((atuais) => [...(atuais ?? []), ...r.itens]);
      setCursor(r.cursor);
      setTemMais(r.temMais);
    } catch (e) {
      setErro(explicarMotivo(e, 'falha ao carregar mais contatos'));
    } finally {
      setCarregandoMais(false);
    }
  }

  /** Rolou até perto do fim: puxa a página seguinte sozinho. */
  function aoRolar({ y }: { x: number; y: number }): void {
    const caixa = listaRef.current;
    if (!caixa || !temMais || carregandoMais) return;
    const restante = caixa.scrollHeight - caixa.clientHeight - y;
    if (restante < 120) void carregarMais();
  }

  const listaRef = useRef<HTMLDivElement>(null);

  return (
    <Group align="flex-start" gap="md" wrap="nowrap" style={{ alignItems: 'stretch' }}>
      {/* ------------------------------------------------------------------
          Coluna esquerda. No celular ela desaparece quando há conversa
          aberta: uma coluna por vez, como num aplicativo de mensagem.
      ------------------------------------------------------------------ */}
      <Box
        w={{ base: '100%', sm: 300 }}
        miw={{ sm: 260 }}
        display={{ base: selecionado ? 'none' : 'block', sm: 'block' }}
        style={{ flexShrink: 0 }}
      >
        <Stack gap="xs">
          <TextInput
            size="xs"
            placeholder={
              podeTelefone ? 'Nome, telefone ou protocolo' : 'Nome ou protocolo (AP-001009)'
            }
            value={busca}
            onChange={(e) => setBusca(e.currentTarget.value)}
            aria-label="Buscar contato"
          />
          <Select
            size="xs"
            allowDeselect={false}
            value={dias}
            onChange={(v) => setDias(v ?? '')}
            data={PERIODOS}
            aria-label="Período"
          />

          {!podeTelefone && (
            <Text size="xs" c="dimmed">
              Seu papel busca por nome e protocolo. Telefone exige permissão de edição — uma caixa
              que confirma “este número falou com o aeroclube” é consulta de telefone por outro
              nome.
            </Text>
          )}

          {desconhecido && (
            <Alert color="yellow" variant="light" p="xs">
              <Text size="xs">
                O estado do bot vive no Redis e ele não respondeu: os indicadores desta lista estão
                <strong> desconhecidos</strong>, o que não é o mesmo que “todas com o bot
                respondendo”.
              </Text>
            </Alert>
          )}

          {erro && (
            <Alert color="red" variant="light" role="alert" p="xs">
              <Stack gap="xs" align="flex-start">
                <Text size="xs">{erro}</Text>
                <Button size="xs" onClick={() => void carregar()}>
                  Tentar de novo
                </Button>
              </Stack>
            </Alert>
          )}

          {!itens && !erro && (
            <Group gap="xs">
              <Loader size="sm" />
              <Text c="dimmed" size="sm">
                Carregando…
              </Text>
            </Group>
          )}

          {itens && itens.length === 0 && (
            <Vazio
              titulo={
                buscaAtiva.trim() ? 'Nenhum contato para esta busca.' : 'Nenhuma conversa ainda.'
              }
            >
              {buscaAtiva.trim()
                ? 'Procure pelo nome como está no cadastro, ou pelo protocolo que o aluno recebeu (AP-001009). O filtro de período também limita a busca.'
                : 'Todo telefone que mandar mensagem ao bot aparece aqui, ordenado pela última mensagem. Enquanto ninguém escrever, esta lista fica vazia — e isso não significa que o bot está fora do ar.'}
            </Vazio>
          )}

          {itens && itens.length > 0 && (
            <ScrollArea.Autosize
              mah="calc(100vh - 260px)"
              viewportRef={listaRef}
              onScrollPositionChange={aoRolar}
              type="auto"
            >
              <Stack gap={2} pr="xs">
                {itens.map((c) => (
                  <LinhaDeContato
                    key={c.atendimentoId}
                    contato={c}
                    ativo={c.atendimentoId === selecionado}
                    desconhecido={desconhecido}
                    aoAbrir={() => {
                      setSelecionado(c.atendimentoId);
                      setAncora(null);
                    }}
                  />
                ))}

                {temMais && (
                  <Button
                    size="xs"
                    variant="subtle"
                    loading={carregandoMais}
                    onClick={() => void carregarMais()}
                  >
                    Carregar mais contatos
                  </Button>
                )}
                {!temMais && (
                  <Text size="xs" c="dimmed" ta="center" py="xs">
                    Fim da lista.
                  </Text>
                )}
              </Stack>
            </ScrollArea.Autosize>
          )}

          {/*
            No pé da coluna esquerda, como na tela de Menu. Esta coluna NÃO
            gruda (só `flexShrink`), então abrir a ajuda empurra a página e
            nada sai do alcance da rolagem — não precisa do encurtamento que a
            árvore do Menu precisou.

            A busca por protocolo já está no campo e no estado vazio; o que
            "encerrar sessão" faz já está na confirmação dela. Os dois são
            referenciados, não reescritos.
          */}
          <BlocoDeAjuda
            secoes={[
              {
                id: 'como',
                titulo: 'Como esta tela funciona',
                corpo: (
                  <>
                    <Text size="sm">
                      <strong>Uma linha por pessoa, não por conversa.</strong> A lista é de
                      contatos: cada telefone aparece uma vez, ordenado pela última mensagem.
                      Quando a mesma pessoa voltou em dias diferentes, a linha mostra{' '}
                      <em>“3 atendimentos”</em> — são três protocolos, e o fio à direita mostra os
                      três em sequência, com uma emenda marcando onde um termina e o outro começa.
                    </Text>
                    <Text size="sm">
                      <strong>O painel não manda mensagem para o aluno.</strong> Em nenhuma tela.
                      Aqui você lê o que foi trocado; falar com a pessoa é pelo WhatsApp, como
                      sempre. Não existe caixa de resposta nesta tela e isso é de propósito.
                    </Text>
                    <Text size="sm">
                      <strong>O indicador diz quem está atendendo aquela conversa.</strong>{' '}
                      <em>● bot ativo</em>: o bot responde sozinho. <em>▲ bot calado</em>: ele
                      parou de responder — ou porque alguém da secretaria escreveu para a pessoa
                      pelo WhatsApp (o bot sai da frente sozinho quando isso acontece), ou porque
                      a conversa foi passada para a secretaria aqui no painel. Conversa com o bot
                      calado espera gente: se ninguém responder, ninguém responde.
                    </Text>
                    <Text size="sm">
                      <strong>“Devolver ao bot” faz o bot voltar a responder</strong> aquela
                      pessoa, do ponto em que ela estava no menu.{' '}
                      <strong>“Passar para a secretaria”</strong> faz o contrário: cala o bot até
                      alguém devolver. As duas são reversíveis e não avisam o aluno.{' '}
                      <em>“Encerrar sessão”</em> é outra coisa, mais grave, e a própria
                      confirmação dela explica o que descarta.
                    </Text>
                    <Text size="sm" c="dimmed">
                      O aluno recebeu um protocolo (AP-001009) na conversa. Colar esse código na
                      busca abre o contato já no ponto certo do histórico — é o caminho mais curto
                      quando alguém liga citando o número.
                    </Text>
                  </>
                ),
              },
            ]}
          />
        </Stack>
      </Box>

      {/* ------------------------------------------------------------------
          Coluna direita: o fio, inteiro, como já estava.
      ------------------------------------------------------------------ */}
      <Box
        style={{ flex: 1, minWidth: 0 }}
        display={{ base: selecionado ? 'block' : 'none', sm: 'block' }}
      >
        {selecionado ? (
          <>
            {/*
              A ação de cadastro vem ANTES do fio, e não dentro dele: ela é
              sobre a PESSOA, não sobre as mensagens. Quem já escreveu tem
              telefone confirmado e costuma estar fora de qualquer lista de
              aviso só por não ter cadastro.
            */}
            <MarcarNaLista
              atendimentoId={selecionado}
              nomeSugerido={itens?.find((c) => c.atendimentoId === selecionado)?.nome ?? null}
              capacidades={capacidades}
            />
            <ConversaCompleta
            key={selecionado}
            atendimentoId={selecionado}
            capacidades={capacidades}
            // No celular o botão volta para a lista; no desktop ele não
            // aparece, porque a lista está do lado.
            aoFechar={() => {
              setSelecionado(null);
              setAncora(null);
              aoMudarPendencias();
            }}
            rotuloFechar="Voltar"
            /* A coluna usa a tela que tem: 60vh (o padrão, feito para o fio
               sobreposto) deixava metade do monitor vazia e o fio curto. O
               desconto cobre a barra do topo, o espaçamento da página e o
               cabeçalho do próprio fio. */
            altura="calc(100vh - 300px)"
            {...(ancora ? { ancora } : {})}
            {...(aoVerDetalheTecnico ? { aoVerDetalheTecnico } : {})}
            />
          </>
        ) : (
          <Vazio titulo="Escolha um contato à esquerda.">
            A conversa aparece aqui, com as mensagens na ordem em que foram trocadas, as emendas
            entre atendimentos e o estado do bot no topo. Se o aluno passou um protocolo, busque por
            ele: a tela abre o contato já no ponto certo do histórico.
          </Vazio>
        )}
      </Box>
    </Group>
  );
}

// ---------------------------------------------------------------------------

function LinhaDeContato({
  contato,
  ativo,
  desconhecido,
  aoAbrir,
}: {
  contato: ContatoNaLista;
  ativo: boolean;
  desconhecido: boolean;
  aoAbrir: () => void;
}): JSX.Element {
  const indicador = contato.estado ? INDICADOR[contato.estado] : null;
  const calado = contato.estado === 'humano' || contato.estado === 'pausado_auto';

  return (
    <UnstyledButton onClick={aoAbrir} aria-current={ativo ? 'true' : undefined}>
      <Paper
        withBorder={ativo}
        p="xs"
        radius="md"
        bg={
          ativo
            ? 'var(--mantine-primary-color-light)'
            : calado
              ? 'var(--mantine-color-orange-light)'
              : undefined
        }
      >
        <Stack gap={2}>
          <Group justify="space-between" gap="xs" wrap="nowrap">
            <Box style={{ minWidth: 0 }}>
              <NomeDoAluno
                nome={contato.nome}
                origem={contato.nomeOrigem}
                semNome={contato.telefoneMascarado ?? '(sem nome)'}
              />
            </Box>
            <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              {quando(contato.ultimaEm)}
            </Text>
          </Group>

          {/* Mascarado na lista, sempre. O completo é no cabeçalho do fio, e
              só para quem tem permissão. */}
          {contato.nome && (
            <Text size="xs" c="dimmed" ff="monospace">
              {contato.telefoneMascarado ?? '—'}
            </Text>
          )}

          <Text size="xs" c="dimmed" lineClamp={1}>
            {contato.ultimaMensagem
              ? `${contato.ultimoAutor === 'bot' ? 'bot: ' : ''}${contato.ultimaMensagem}`
              : 'sem mensagem gravada'}
          </Text>

          <Group gap={6} wrap="wrap">
            {/* Quem é a pessoa, antes do estado do bot: muda como se lê o
                resto da linha. Sem o SAGA, o selo diz "interessado". */}
            <OrigemDoContato tipo={contato.tipo} curso={contato.curso} noSaga={contato.noSaga} />
            {/* Ícone E texto: a cor é reforço, nunca o sinal. */}
            {indicador && (
              <Badge size="xs" color={indicador.cor} variant={calado ? 'filled' : 'light'}>
                {indicador.icone} {indicador.rotulo}
              </Badge>
            )}
            {desconhecido && (
              <Text size="xs" c="dimmed">
                estado desconhecido
              </Text>
            )}
            {contato.modo === 'shadow' && (
              <Badge size="xs" color="orange" variant="light">
                sombra
              </Badge>
            )}
            {contato.atendimentos > 1 && (
              <Text size="xs" c="dimmed">
                {contato.atendimentos} atendimentos
              </Text>
            )}
          </Group>
        </Stack>
      </Paper>
    </UnstyledButton>
  );
}
